/**
 * @file 预约充电桩组件 - VIP会员页专用
 * @description 展示用户预约权益、附近充电站列表，支持预约操作
 */

import React, { useMemo, useCallback } from "react";
import { NativeByUrlList, goToNativePage } from "@/utils/common";
import { trackGdp } from "@/utils/gio-report";
import { useUserContext } from "../../routes/home/userContext";
import { useConfigPage } from "@/hooks/useConfigPage/useConfigPage";
import { getReportVipData, entry } from "../../utils";
import BookingPrivilege, { PrivilegeItem } from "./components/BookingPrivilege";
import StationList, { StationItemData } from "./components/StationList";
import BookingButton from "./components/BookingButton";
import { getBookingButtonState } from "./utils/bookingUtils";
import useBookingHandler from "./hooks/useBookingHandler";
import "./index.less";

/**
 * 无限预约次数的阈值
 * 当配额 >= 此值时，视为无限次
 */
const UNLIMITED_QUOTA_THRESHOLD = 30;

/**
 * 判断是否为无限预约次数
 * 规则：未定义（undefined/null）或 >= 30 次视为无限
 */
const isUnlimitedQuota = (quota: number | null | undefined): boolean => {
  return quota === undefined || quota === null || quota >= UNLIMITED_QUOTA_THRESHOLD;
};

/**
 * 将 levelInfo 数据转换为权益卡片数据
 * 固定生成 3 个等级（V1、V2、V3），当 monthlyBookingLimitMap 中某个等级的字段为空或 >= 30 时，预约次数为无限次（-1）
 */
function transformToPrivilegeList(levelInfo: any): {
  privileges: PrivilegeItem[];
  currentLevelIndex: number;
} {
  const { monthlyBookingLimitMap = {}, currentMonthBookingTimes, carVoltageType, level } = levelInfo || {};

  const voltageText = carVoltageType === 1 ? "高压" : "低压";
  let currentLevelIndex = 0;

  // 固定 3 个等级
  const levels = [1, 2, 3];

  const privileges = levels.map((levelKey, index) => {
    const isCurrentLevel = levelKey === level;
    if (isCurrentLevel) {
      currentLevelIndex = index;
    }

    // 从 map 中获取配额，未定义或 >= 30 时为无限次（-1）
    const quota = monthlyBookingLimitMap[levelKey];
    const monthlyQuota = isUnlimitedQuota(quota) ? -1 : quota;
    const usedCount = isCurrentLevel ? (currentMonthBookingTimes || 0) : 0;
    const remainingCount = monthlyQuota === -1 ? -1 : Math.max(0, monthlyQuota - usedCount);

    return {
      level: levelKey,
      levelName: `${voltageText}小鹏车主V${levelKey}等级专享`,
      levelType: `V${levelKey}`,
      monthlyQuota,
      usedCount,
      remainingCount,
      isCurrentLevel,
    };
  });

  return { privileges, currentLevelIndex };
}

/**
 * 组件配置接口 - 从 comp.moduleJsonData 解析
 */
interface ModuleJsonData {
  appointmentSetting?: {
    visibleRange?: number;
    visibleCount?: number;
    isLimit?: boolean;
    jumpUrlMap?: Record<string, string>;
    defaultImg?: string;
  };
  componentVision?: {
    bgUrl?: string;
    spacing?: number;
  };
}

interface BookPileProps {
  comp?: {
    moduleId?: string;
    moduleType?: string;
    moduleJsonData?: ModuleJsonData;
  };
}

/**
 * 预约充电桩组件 - VIP会员页专用
 */
const BookPile: React.FC<BookPileProps> = ({ comp }) => {
  const { levelInfo, userType, isLoading } = useUserContext();
  const { configPageQuery } = useConfigPage();

  // 从配置页获取版本信息
  const versionId = configPageQuery?.data?.versionCode || "";
  const componentCode = comp?.moduleId || "";

  // 解析组件配置
  const moduleJsonData = comp?.moduleJsonData;
  const appointmentSetting = moduleJsonData?.appointmentSetting || {};
  const componentVision = moduleJsonData?.componentVision || {};

  const stationConfig = useMemo(() => ({
    visibleRange: appointmentSetting?.visibleRange || 10000,
    visibleCount: appointmentSetting?.visibleCount || 1,
    isLimit: appointmentSetting?.isLimit ?? false,
    jumpUrlMap: appointmentSetting?.jumpUrlMap || {},
    emptyImageUrl: appointmentSetting?.defaultImg || "",
  }), [appointmentSetting]);

  const bgUrl = componentVision?.bgUrl || "";
  const spacing = componentVision?.spacing || 0; // 上边距配置

  // 从 context 中的 levelInfo 转换权益数据
  const { privileges, currentLevelIndex } = useMemo(() => {
    return transformToPrivilegeList(levelInfo);
  }, [levelInfo]);

  /**
   * 处理轮播切换
   * 用于埋点追踪用户切换等级卡片行为
   * @param index - 切换后的卡片索引
   */
  const handleSwiperChange = useCallback((index: number) => {
    const privilege = privileges[index];
    if (privilege && levelInfo) {
      trackGdp("P11222", "B036", {
        ...getReportVipData(levelInfo),
        versionId,
        ComponentCode: componentCode,
        description: "充电VIP主页_预约组件_轮播切换",
        var_ch: entry(),
        userType,
        toIndex: index,
        levelType: privilege.levelType,
      });
    }
  }, [privileges, levelInfo, versionId, componentCode, userType]);

  /**
   * 处理预约记录点击
   * 埋点: B039
   * 跳转到预约订单列表页（tab=1 表示预约订单）
   */
  const handleRecordClick = () => {
    if (levelInfo) {
      const vipData = getReportVipData(levelInfo) as {
        vip_level: string;
        vip_protection: string;
        vip_validity: string;
      };
      trackGdp("P11222", "B039", {
        versionId,
        ComponentCode: componentCode,
        var_ch: entry(),
        userType,
        vip_level: vipData.vip_level,
        vip_protection: vipData.vip_protection,
        vip_validity: vipData.vip_validity,
      });
    }

    const { origin } = window.location;
    goToNativePage({
      app: "xiaopeng://charging/orders?tab=1",
      appParams: {
        tab: 1,
      },
      mini: "/charging/pages/order-list/index?tab=1",
      browser: `${origin}/apps/charge-web/jump-to-wx-mini.html?page=charging/pages/order-list/index&query=tab%3D1`,
    });
  };

  /**
   * 处理查看更多点击
   * 埋点: B038
   * 跳转到充电站列表页
   */
  const handleViewMoreClick = () => {
    if (levelInfo) {
      const vipData = getReportVipData(levelInfo) as {
        vip_level: string;
        vip_protection: string;
        vip_validity: string;
      };
      trackGdp("P11222", "B038", {
        versionId,
        ComponentCode: componentCode,
        var_ch: entry(),
        userType,
        vip_level: vipData.vip_level,
        vip_protection: vipData.vip_protection,
        vip_validity: vipData.vip_validity,
      });
    }

    if (stationConfig.jumpUrlMap) {
      NativeByUrlList(stationConfig.jumpUrlMap);
    }
  };

  /**
   * 处理授权定位按钮点击
   * 用于埋点追踪用户授权定位行为
   */
  const handleAuthLocationClick = () => {
    if (levelInfo) {
      trackGdp("P11222", "B036", {
        ...getReportVipData(levelInfo),
        versionId,
        ComponentCode: componentCode,
        description: "充电VIP主页_预约组件_授权定位",
        var_ch: entry(),
        userType,
      });
    }
  };

  /**
   * 处理充电站卡片点击
   * 用于埋点追踪用户点击充电站行为
   * @param station - 被点击的充电站数据
   */
  const handleStationClick = (station: StationItemData) => {
    if (levelInfo) {
      trackGdp("P11222", "B036", {
        ...getReportVipData(levelInfo),
        versionId,
        ComponentCode: componentCode,
        description: "充电VIP主页_预约组件_充电站点击",
        var_ch: entry(),
        userType,
        stationId: station.stationId,
      });
    }
  };

  // 使用预约处理器 Hook
  const { handleBookingClick } = useBookingHandler({
    versionId,
    componentCode,
    levelInfo,
    userType,
  });

  // 获取当前用户的剩余预约次数
  const currentRemainingCount = useMemo(() => {
    const currentPrivilege = privileges[currentLevelIndex];
    return currentPrivilege?.remainingCount ?? 0;
  }, [privileges, currentLevelIndex]);

  // 判断是否有预约机会（直接使用接口返回的 hasBookingTimes 字段）
  const hasBookingOpportunity = levelInfo?.hasBookingTimes ?? false;

  /**
   * 渲染预约按钮
   * 根据剩余预约次数和设备空闲情况决定按钮状态
   * 当用户没有预约机会时，不展示按钮
   * @param station - 充电站数据
   * @returns 预约按钮 JSX 或 null
   */
  const renderBookingButton = useCallback(
    (station: StationItemData) => {
      return (
        <BookingButton
          onClick={() => handleBookingClick(station)}
        />
      );
    },
    [handleBookingClick]
  );

  return (
    <div
      className="book-pile-container"
      style={{
        paddingTop: spacing ? `${spacing}px` : undefined,
        backgroundImage: bgUrl ? `url(${bgUrl})` : "none",
        backgroundSize: "100% auto",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* 头部区域 */}
      <div className="book-pile-header">
        <div className="book-pile-header-record" onClick={handleRecordClick}>
          <span className="record-text">预约记录</span>
        </div>
      </div>

      {/* 整体卡片区域：预约权益 + 站点 + 查看更多 */}
      <div className="book-pile-card">
        {/* 预约权益区 */}
        <div className="book-pile-privilege-section">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner" />
              <span>加载中...</span>
            </div>
          ) : (
            <BookingPrivilege
              privileges={privileges}
              currentLevelIndex={currentLevelIndex}
              userLevelInfo={levelInfo}
              onSwiperChange={handleSwiperChange}
            />
          )}
        </div>

        {/* 场站列表区 */}
        <div className="book-pile-station-section">
          <StationList
            config={{
              radius: stationConfig.visibleRange,
              limitNum: stationConfig.visibleCount,
              isLimit: stationConfig.isLimit,
              emptyImageUrl: stationConfig.emptyImageUrl,
              jumpUrlMap: stationConfig.jumpUrlMap,
            }}
            componentCode={componentCode}
            hasBookingOpportunity={hasBookingOpportunity}
            onViewMoreClick={handleViewMoreClick}
            onStationClick={handleStationClick}
            onAuthLocationClick={handleAuthLocationClick}
            renderButton={renderBookingButton}
          />
        </div>
      </div>
    </div>
  );
};

export default BookPile;
